<script setup lang="ts">
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import type Lenis from 'lenis'

const localePath = useLocalePath()
const { t, localeProperties } = useI18n()
const { $lenis } = useNuxtApp() as unknown as { $lenis?: Lenis }
const dirX = useDirX()

const gsapActive = ref(false)

const steps = computed(() => [
  {
    num: '1',
    titleKey: 'howItWorks.step1_title',
    descKey: 'howItWorks.step1_desc',
    image: '/overview/sign_in.svg',
    bg: 'bg-persian-green-50',
    badgeBg: 'bg-persian-green-500',
    badgeText: 'text-white',
    rotate: 'rotate(-3deg)',
  },
  {
    num: '2',
    titleKey: 'howItWorks.step2_title',
    descKey: 'howItWorks.step2_desc',
    image: '/overview/setup.svg',
    bg: 'bg-pink-50',
    badgeBg: 'bg-heart-purple',
    badgeText: 'text-white',
    rotate: 'rotate(4deg)',
  },
  {
    num: '3',
    titleKey: 'howItWorks.step3_title',
    descKey: 'howItWorks.step3_desc',
    image: '/overview/appointment-scheduling.svg',
    bg: 'bg-sky-50',
    badgeBg: 'bg-sky-blue',
    badgeText: 'text-white',
    rotate: 'rotate(-4deg)',
  },
])

const section = ref<HTMLElement | null>(null)
const pinTarget = ref<HTMLElement | null>(null)
const activeStep = ref(0)

// Section background washes through each step's accent. On desktop the active
// step is scroll-driven; on mobile it's tap-driven (see selectStep).
const sectionBgClass = computed(() => steps.value[activeStep.value]?.bg ?? 'bg-white')

// Maps a unit rect onto the laptop screen's parallelogram. The frame img is
// mirrored in RTL (rtl:-scale-x-100), so the screen leans the other way; we use
// the (637−x) mirrored corners here so screenshots still fill it while their own
// content stays un-mirrored (readable).
const screenMatrix = computed(() =>
  localeProperties.value.dir === 'rtl'
    ? 'matrix(402 0 -34 234 148 11)'
    : 'matrix(402 0 34 234 86 11)',
)

let pinST: ReturnType<typeof ScrollTrigger.create> | null = null
let starSpinTween: ReturnType<typeof gsap.to> | null = null

// Re-position the decorative star opposite the active card. We anchor the star
// at top:0 / left:0 and translate it with GSAP, since CSS transitions can't
// smoothly interpolate between `right: 2rem` and `right: auto` when the star
// flips sides between alternating cards.
async function positionStar(animate = true) {
  await nextTick()
  // Wait one frame so flex's margin-auto cross-axis layout has settled —
  // otherwise in RTL the cards' physical positions can read as 0 before the
  // browser finishes applying me-auto / ms-auto, and the star ends up on the
  // same side as the active card (then hidden behind it via z-index).
  await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))

  const starEl = section.value?.querySelector<HTMLElement>('.hiw-star')
  const stepsEl = section.value?.querySelector<HTMLElement>('.hiw-steps')
  const cards = stepsEl?.querySelectorAll<HTMLElement>('.hiw-step')
  if (!starEl || !stepsEl || !cards?.length) return
  const card = cards[activeStep.value]
  if (!card) return

  // getBoundingClientRect always reports the actual rendered physical-left
  // position regardless of writing direction, unlike offsetLeft which can
  // be inconsistent in RTL flex layouts.
  const stepsRect = stepsEl.getBoundingClientRect()
  const cardRect = card.getBoundingClientRect()
  const stepsWidth = stepsRect.width
  const cardLeft = cardRect.left - stepsRect.left
  const cardCenter = cardLeft + cardRect.width / 2

  const starSize = starEl.offsetWidth || 56
  const padding = 12

  // Card sides alternate via me-auto / ms-auto, so which half of the steps
  // column the card sits in tells us which side to place the star on (opposite).
  const cardOnPhysicalStart = cardCenter < stepsWidth / 2
  const starX = cardOnPhysicalStart
    ? stepsWidth - starSize - padding
    : padding
  const starY = (cardRect.top - stepsRect.top) + (cardRect.height / 2) - (starSize / 2)

  const vars = { x: starX, y: starY }
  if (animate) gsap.to(starEl, { ...vars, duration: 0.6, ease: 'power3.out' })
  else gsap.set(starEl, vars)
}

function onResize() {
  positionStar(true)
}

onMounted(async () => {
  gsapActive.value = window.innerWidth >= 1024
  await nextTick()

  const el = section.value
  if (!el) return

  const dir = dirX.value

  // Left column entrance — fires before the pin locks
  gsap.fromTo(el.querySelector('.hiw-left'),
    { opacity: 0, x: -70 * dir },
    {
      opacity: 1, x: 0, duration: 1, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 78%', once: true },
    },
  )

  // Decorative star — spin in once the cards come into view. Position is
  // controlled separately via positionStar() so x/y stay free for the swap.
  positionStar(false)
  window.addEventListener('resize', onResize)
  gsap.fromTo(el.querySelector('.hiw-star'),
    { opacity: 0, scale: 0, rotation: -180 },
    {
      opacity: 1, scale: 1, rotation: 0, duration: 0.9, ease: 'back.out(2)',
      scrollTrigger: { trigger: el.querySelector('.hiw-steps'), start: 'top 82%', once: true },
      onComplete: () => {
        const starEl = el.querySelector<HTMLElement>('.hiw-star')
        if (starEl) {
          starSpinTween = gsap.to(starEl, { rotation: '+=360', duration: 7, ease: 'none', repeat: -1 })
        }
      },
    },
  )

  // Arrow + CTA live below the pin, so they animate as it releases
  gsap.fromTo(el.querySelector('.hiw-arrow'),
    { opacity: 0, y: -20, rotation: -30 },
    {
      opacity: 1, y: 0, rotation: 0, duration: 0.7, ease: 'back.out(1.6)',
      scrollTrigger: { trigger: el.querySelector('.hiw-arrow'), start: 'top 92%', once: true },
    },
  )
  gsap.fromTo(el.querySelector('.hiw-cta'),
    { opacity: 0, scale: 0.88, y: 20 },
    {
      opacity: 1, scale: 1, y: 0, duration: 0.65, ease: 'back.out(1.4)',
      scrollTrigger: { trigger: el.querySelector('.hiw-cta'), start: 'top 92%', once: true },
    },
  )

  if (!gsapActive.value) {
    // Mobile: original stagger entrance, no pin
    gsap.fromTo(el.querySelectorAll('.hiw-step'),
      { opacity: 0, x: 80 * dir, scale: 0.85 },
      {
        opacity: 1, x: 0, scale: 1, duration: 0.8, ease: 'back.out(1.8)', stagger: 0.18,
        // Release the inline opacity afterwards so the cards settle at full
        // opacity (mobile cards have no active/dim state).
        clearProps: 'opacity',
        scrollTrigger: { trigger: el.querySelector('.hiw-steps'), start: 'top 82%', once: true },
      },
    )
    return
  }

  // Desktop: pin the section while the user steps through. Each step gets ~500px
  // of scroll travel; the active index is derived from scroll progress so the
  // image swap, card highlight, and connector draw stay in lockstep.
  const pin = pinTarget.value
  if (!pin) return

  const stepCount = steps.value.length
  const scrollDist = stepCount * 500

  pinST = ScrollTrigger.create({
    trigger: pin,
    start: 'top top',
    end: `+=${scrollDist}`,
    pin: true,
    scrub: 1,
    anticipatePin: 1,
    // Higher refreshPriority refreshes FIRST. Pinned triggers MUST refresh
    // before later sections so their pin spacers are inserted before those
    // triggers calculate start/end positions.
    refreshPriority: 1,
    onUpdate: (self) => {
      // Map progress to step index. Clamp so a fully-progressed pin stays on
      // the last step rather than overflowing.
      const idx = Math.min(Math.floor(self.progress * stepCount), stepCount - 1)
      if (activeStep.value !== idx) activeStep.value = idx
    },
  })

  // The pin spacer GSAP inserts changes document height; Lenis caches scrollMax
  // against the old layout, so a manual resize+refresh is required.
  $lenis?.resize()
  ScrollTrigger.refresh()

  if (document.readyState !== 'complete') {
    window.addEventListener('load', () => {
      $lenis?.resize()
      ScrollTrigger.refresh()
    }, { once: true })
  }
})

// Re-tween the star whenever the active step changes
watch(activeStep, () => positionStar(true))

onUnmounted(() => {
  starSpinTween?.kill()
  starSpinTween = null
  pinST?.kill()
  pinST = null
  window.removeEventListener('resize', onResize)
})
</script>

<template>
  <section id="how" ref="section" :class="['transition-colors duration-700 ease-out', sectionBgClass]">
    <!-- Pinned wrapper: justify-center groups content + CTA as one block in the
         vertical center of the viewport, so any leftover space sits above and
         below the group rather than between the cards and the CTA. -->
    <div
      ref="pinTarget"
      :class="[
        'hiw-pin flex flex-col justify-center',
        gsapActive ? 'min-h-screen pt-24 pb-10 lg:pt-28 lg:pb-14' : 'pt-24 pb-12 md:pt-28 md:pb-16 lg:pt-32 lg:pb-20',
      ]">
      <div class="container w-full">
        <div>
          <i18n-t
              keypath="howItWorks.heading"
              tag="h2"
              class="text-[clamp(1.75rem,3.5vw,3rem)] font-bold leading-[1.25] text-deep-indigo mb-3">
              <template #n>
                <span class="text-heart-purple">3</span>
              </template>
            </i18n-t>
            <p class="text-base md:text-lg font-medium text-overcast-night leading-[1.6] mb-1.5">
              {{ t('howItWorks.subheading') }}
            </p>
            <p class="text-sm md:text-base text-overcast-night mb-4">
              {{ t('howItWorks.body') }}
            </p>
        </div>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-16 items-center w-full">

          <!-- Left: heading + per-step illustration (desktop only — on mobile the
               warped laptop screenshot adds height without the scroll-driven step
               sync that makes it meaningful) -->
          <div class="hiw-left hidden lg:flex flex-col items-center lg:items-start text-center lg:text-start">
            <!-- Static laptop frame with the per-step screenshot warped into its
                 tilted screen. The frame img sizes the wrapper; an overlay SVG
                 sharing screen.svg's 637×380 viewBox maps each screenshot onto
                 the screen's parallelogram (corners TL/TR/BL → matrix below),
                 cross-fading between steps by activeStep. -->
            <div class="hiw-screen relative w-full max-w-xl mx-auto lg:mx-0 lg:mb-8 mt-8">
              <img src="/illustrations/screen.svg" alt="" class="w-full block rtl:-scale-x-100" aria-hidden="true">
              <svg
                viewBox="0 0 637 380"
                class="absolute inset-0 w-full h-full"
                role="img"
                :aria-label="t(steps[activeStep].titleKey)">
                <image
                  v-for="(s, i) in steps"
                  :key="s.image"
                  :href="s.image"
                  width="1"
                  height="1"
                  preserveAspectRatio="none"
                  :transform="screenMatrix"
                  :class="['transition-opacity duration-500 ease-out', activeStep === i ? 'opacity-100' : 'opacity-0']" />
              </svg>
            </div>
          </div>

          <!-- Right: step cards -->
          <div class="relative hiw-steps">
            <img src="/illustrations/star-blue.svg" alt="" class="hiw-star hidden lg:block absolute top-0 left-0 w-14 h-14 z-10 will-change-transform" aria-hidden="true">

            <div class="flex flex-col pt-6 lg:pt-10">
              <div
                v-for="(step, i) in steps"
                :key="i"
                :class="[
                  'hiw-step relative border-2 border-gray-900 rounded-md px-6 py-5 flex items-start gap-4 transition-all duration-500 ease-out',
                  step.bg,
                  gsapActive
                    ? [
                      'w-[72%]',
                      i > 0 ? 'mt-1' : '',
                      i % 2 === 0 ? 'me-auto' : 'ms-auto',
                      activeStep !== i
                        ? 'shadow-[5px_5px_0_#111827] opacity-55'
                        : 'shadow-[8px_8px_0_#111827] opacity-100',
                    ]
                    : [
                      'w-full shadow-[6px_6px_0_#111827]',
                      i > 0 ? 'mt-4' : '',
                    ],
                ]"
                :style="gsapActive ? {
                  transform: activeStep === i ? `${step.rotate} scale(1.05)` : step.rotate,
                  zIndex: activeStep === i ? 20 : steps.length - i,
                } : {}">
                <div :class="['w-9 h-9 rounded-sm border border-gray-900 shadow-[2px_2px_0_#111827] flex items-center justify-center font-bold text-base shrink-0 text-gray-900', step.badgeBg]">
                  {{ step.num }}
                </div>
                <div>
                  <div class="text-base md:text-lg font-bold text-gray-900 mb-1.5">{{ t(step.titleKey) }}</div>
                  <div class="text-sm text-overcast-night leading-[1.6]">{{ t(step.descKey) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Arrow + CTA sit at the bottom of the pinned viewport -->
      <div class="container mt-8 md:mt-10">
        <div class="flex items-center flex-col justify-center w-full gap-6">
          <div class="flex justify-start ps-6">
            <img src="/illustrations/arrow.svg" alt="" class="hiw-arrow w-20 h-20" aria-hidden="true">
          </div>

          <div class="hiw-cta flex items-center gap-5">
            <img src="/illustrations/star-yellow.svg" alt="" class="w-10 h-10" aria-hidden="true">
            <a v-magnetic :href="localePath('/contact-sales')" class="bfm-btn bfm-btn-md bg-cheese-yellow gap-2.5">
              {{ t('howItWorks.cta') }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Short desktop viewports (small laptops, or a wide monitor viewed at 125%+
   scale where innerHeight shrinks): compress the pinned two-column layout so it
   fits within one screen instead of overflowing into — and colliding with — the
   next pinned section. Scoped selectors carry the [data-v] attribute, so they
   outrank the equal-specificity Tailwind utilities they override. Gated on
   min-width:1024px so only the pinned desktop layout is affected. */
@media (min-width: 1024px) and (max-height: 860px) {
  .hiw-pin {
    padding-top: 5rem;
    padding-bottom: 2rem;
  }

  .hiw-screen {
    max-width: 30rem;
    margin-top: 1rem;
    margin-bottom: 1rem;
  }

  .hiw-step {
    padding-top: 0.875rem;
    padding-bottom: 0.875rem;
  }

  .hiw-arrow {
    width: 4rem;
    height: 4rem;
  }
}

@media (min-width: 1024px) and (max-height: 720px) {
  .hiw-pin {
    padding-top: 4rem;
    padding-bottom: 1.5rem;
  }

  .hiw-screen {
    max-width: 26rem;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .hiw-step {
    padding-top: 0.625rem;
    padding-bottom: 0.625rem;
  }

  .hiw-arrow {
    width: 3rem;
    height: 3rem;
  }
}
</style>
